import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container, Paper, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/api';
import { GAEvents } from '../hooks/useGoogleAnalytics';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();

  const handleLogout = () => {
    // Google Analytics: ログアウトイベントを送信
    GAEvents.logout();
    
    logout();
    navigate('/');
  };

  // 広告枠コンポーネント
  const AdSpace: React.FC<{ width?: string; height?: string; position?: string }> = ({ 
    width = '160px', 
    height = '600px', 
    position = 'side' 
  }) => (
    <Paper 
      sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ccc',
        color: '#666',
        fontSize: { 
          xs: position === 'bottom' ? '12px' : '10px', // モバイルでフォントサイズを小さく
          sm: position === 'bottom' ? '16px' : '14px' 
        },
        fontWeight: 'bold',
        textAlign: 'center',
        p: { xs: 1, sm: 2 } // モバイルでパディングを小さく
      }}
    >
      広告募集中
    </Paper>
  );

  return (
    <>
      <AppBar 
        position="static"
        sx={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 50%, rgba(241,245,249,0.9) 100%)', // グラデーション背景
          backdropFilter: 'blur(20px)', // ガラスモーフィズム効果
          WebkitBackdropFilter: 'blur(20px)', // Safari対応
          color: '#1E293B', // より深いダークグレー
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)', // 近未来的なシャドウ
          border: '1px solid rgba(255, 255, 255, 0.18)', // 半透明ボーダー
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), transparent)', // 上部のアクセントライン
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)', // パーティクル風効果
            pointerEvents: 'none',
          },
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: '56px', sm: '64px' },
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent)', // 下部のサブトルライン
          }
        }}>
          <Box 
            component="img"
            src="/あの街くる街.png"
            alt="あの街くる街"
            sx={{ 
              height: { xs: 32, sm: 40 }, // モバイルでロゴを小さく
              cursor: 'pointer', 
              mr: { xs: 1, sm: 2 },
              borderRadius: '12px', // より大きな角丸
              backgroundColor: 'rgba(255, 255, 255, 0.1)', // 半透明背景
              padding: '6px', // パディングを増加
              border: '1px solid rgba(255, 255, 255, 0.2)', // 半透明ボーダー
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // スムーズなアニメーション
              '&:hover': {
                transform: 'translateY(-2px) scale(1.05)', // ホバー時に浮遊＆拡大
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', // ドロップシャドウ
                border: '1px solid rgba(59, 130, 246, 0.3)', // 青いボーダーでアクセント
              }
            }} 
            onClick={() => navigate('/')}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 2 }, // モバイルでボタン間隔を狭く
            '& .MuiButton-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }, // モバイルでフォントサイズを小さく
              padding: { xs: '6px 12px', sm: '8px 16px' }, // パディング調整
              minWidth: { xs: 'auto', sm: '64px' },
              color: '#1E293B', // 深いダークグレー
              fontWeight: 500,
              borderRadius: '16px', // ピル型ボタン
              backgroundColor: 'rgba(255, 255, 255, 0.1)', // 半透明背景
              border: '1px solid rgba(255, 255, 255, 0.2)', // 半透明ボーダー
              backdropFilter: 'blur(10px)', // ガラス効果
              WebkitBackdropFilter: 'blur(10px)', // Safari対応
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // スムーズなアニメーション
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover': {
                transform: 'translateY(-1px)', // 浮遊効果
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.4)', // 青いアクセント
                '&::before': {
                  left: '100%', // シマー効果
                }
              },
              '&:active': {
                transform: 'translateY(0)', // クリック時の効果
              }
            }
          }}>
            <Button onClick={() => navigate('/')}>
              ホーム
            </Button>
            {isAuthenticated ? (
              <>
                <Button onClick={() => navigate('/reviews')}>
                  口コミ
                </Button>
                {user?.role === 'admin' && (
                  <Button onClick={() => navigate('/admin')}>
                    管理画面
                  </Button>
                )}
                <Button onClick={() => navigate('/profile')}>
                  {user?.nickname}
                </Button>
                <Button onClick={handleLogout}>
                  ログアウト
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => navigate('/reviews')}>
                  口コミ
                </Button>
                <Button onClick={() => navigate('/login')}>
                  ログイン
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* メインコンテンツエリア（両サイド広告付き） */}
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
        {/* 左サイド広告 */}
        <Box sx={{ 
          display: { xs: 'none', lg: 'block' }, 
          width: '180px', 
          p: 2,
          position: 'sticky',
          top: '20px',
          height: 'fit-content'
        }}>
          <AdSpace />
        </Box>
        
        {/* メインコンテンツ */}
        <Container 
          maxWidth="lg" 
          sx={{ 
            mt: { xs: 2, sm: 4 }, // モバイルでマージンを小さく
            mb: { xs: 2, sm: 4 }, 
            flex: 1,
            px: { xs: 1, sm: 3 } // モバイルで左右パディングを調整
          }}
        >
          {children}
          
          {/* ページ下部広告 */}
          <Box sx={{ 
            mt: { xs: 2, sm: 4 }, 
            display: 'flex', 
            justifyContent: 'center',
            // モバイルでは広告を小さく表示
            '& > div': {
              width: { xs: '100%', sm: '728px' },
              maxWidth: { xs: '350px', sm: '728px' },
              height: { xs: '60px', sm: '90px' }
            }
          }}>
            <AdSpace width="728px" height="90px" position="bottom" />
          </Box>
        </Container>
        
        {/* 右サイド広告 */}
        <Box sx={{ 
          display: { xs: 'none', lg: 'block' }, 
          width: '180px', 
          p: 2,
          position: 'sticky',
          top: '20px',
          height: 'fit-content'
        }}>
          <AdSpace />
        </Box>
      </Box>
      
      {/* フッター */}
      <Box component="footer" sx={{ 
        mt: 'auto', 
        py: { xs: 2, sm: 3 }, // モバイルでパディングを小さく
        px: { xs: 1, sm: 2 }, 
        backgroundColor: '#FAFAFA', // より薄いグレー背景
        borderTop: '1px solid #E5E7EB', // 薄いボーダー
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: { xs: 2, sm: 3 }, // モバイルでリンク間隔を狭く
            flexWrap: 'wrap' // モバイルで折り返し可能に
          }}>
            <Link 
              component="button" 
              variant="body2" 
              onClick={() => navigate('/')}
              sx={{ 
                cursor: 'pointer', 
                textDecoration: 'none', 
                fontSize: { xs: '0.75rem', sm: '0.875rem' }, // モバイルでフォントサイズを小さく
                color: '#6B7280', // グレーテキスト
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#2E3440' // ホバー時にダークグレー
                } 
              }}
            >
              ホーム
            </Link>
            <Link 
              component="button" 
              variant="body2" 
              onClick={() => navigate('/reviews')}
              sx={{ 
                cursor: 'pointer', 
                textDecoration: 'none', 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: '#6B7280',
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#2E3440'
                } 
              }}
            >
              口コミ
            </Link>
            {isAuthenticated && (
              <Link 
                component="button" 
                variant="body2" 
                onClick={handleLogout}
                sx={{ 
                  cursor: 'pointer', 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: '#6B7280',
                  '&:hover': { 
                    textDecoration: 'underline',
                    color: '#2E3440'
                  } 
                }}
              >
                ログアウト
              </Link>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Layout;
